import type {
  ValidationErrorDto,
  ValidationResultDto,
} from '@api/processes';

/**
 * Single validation issue surfaced in the editor. Sourced exclusively
 * from the backend (via {@link fromBackendValidationDto}) — there is
 * no client-side validation pass any more; the editor delegates
 * everything to {@code POST /processes/:id/validate}.
 *
 * <p>{@code kind} mirrors the backend's
 * {@link ValidationErrorDto#code} verbatim. Kept as a plain
 * {@code string} so a new backend rule lights up in the panel with no
 * FE change; consumers (icon registry, tooltip lookup) fall back to a
 * generic glyph for codes they don't recognise.</p>
 */
export interface ValidationError {
  readonly nodeId: string | null;
  /** Edge the error attaches to (null for graph- or node-level errors). */
  readonly edgeId?: string | null;
  /** Port the error attaches to (null for graph- / node- / edge-level errors). */
  readonly portId?: string | null;
  readonly kind: string;
  /** One-liner suitable for a list row: e.g. `Task "Repair" has no upstream input`. */
  readonly message: string;
}

export interface ValidationResult {
  /** Draft-valid, i.e. runnable. NOT the publish gate — see {@link publishBlockers}. */
  readonly valid: boolean;
  readonly errors: readonly ValidationError[];
  /**
   * Advisory findings that never block publish/run — surfaced separately
   * from {@link errors} so the runnable chip, node card state, and publish
   * gate stay error-driven while the legend can still show the heads-up.
   */
  readonly warnings: readonly ValidationError[];
  /**
   * Findings that block Publish but not a draft run: no Input/Output node,
   * an unwired port, an unreachable node. Half-wired is the normal shape of
   * a graph being authored, so these render as amber hints while the
   * process is still a draft.
   */
  readonly publishBlockers: readonly ValidationError[];
}

/** Empty result used while the very first detail fetch is in flight. */
export const EMPTY_VALIDATION: ValidationResult = {
  valid: true,
  errors: [],
  warnings: [],
  publishBlockers: [],
};

/** Adapter from the backend wire DTO to the FE-facing renderer shape. */
export function fromBackendValidationDto(
  dto: ValidationResultDto,
): ValidationResult {
  return {
    valid: dto.valid,
    errors: dto.errors.map(fromBackendErrorDto),
    warnings: (dto.warnings ?? []).map(fromBackendErrorDto),
    publishBlockers: (dto.publishBlockers ?? []).map(fromBackendErrorDto),
  };
}

function fromBackendErrorDto(dto: ValidationErrorDto): ValidationError {
  return {
    nodeId: dto.nodeId,
    edgeId: dto.edgeId ?? null,
    portId: dto.portId ?? null,
    kind: dto.code,
    message: dto.message,
  };
}

/**
 * Index validation errors by {@code nodeId} for per-node badges /
 * inspector lookups. Errors with a {@code null} nodeId (graph-level)
 * are excluded — callers that want them iterate {@code result.errors}
 * directly.
 */
export function errorsByNodeId(
  result: ValidationResult,
): Readonly<Record<string, readonly ValidationError[]>> {
  const out: Record<string, ValidationError[]> = {};
  for (const e of result.errors) {
    if (!e.nodeId) continue;
    out[e.nodeId] ??= [];
    out[e.nodeId].push(e);
  }
  return out;
}

/** Minimal edge shape needed by {@link errorsByAffectedNodeId}. */
export interface EdgeEndpoints {
  readonly id: string;
  readonly source: string;
  readonly target: string;
}

/**
 * Like {@link errorsByNodeId}, but also attributes edge-anchored errors
 * (such as {@code PORT_TYPE_MISMATCH}) to the edge's TARGET node so
 * per-node consumers — the {@code NodeHeaderRunButton} warning state,
 * the run gate — refuse to run a node whose incoming wire is broken.
 *
 * <p>Why target only: an edge mismatch means the target cannot safely
 * consume what the source emits. The source node itself is still valid
 * and can be run on its own to produce its output. Attaching to both
 * would needlessly disable a source the user might still want to test.
 */
export function errorsByAffectedNodeId(
  result: ValidationResult,
  edges: readonly EdgeEndpoints[],
): Readonly<Record<string, readonly ValidationError[]>> {
  return groupByAffectedNodeId(result.errors, edges);
}

/**
 * The bucket-agnostic core of {@link errorsByAffectedNodeId}, so the same
 * node attribution can be applied to {@link ValidationResult.publishBlockers}
 * without pretending they are errors.
 */
export function groupByAffectedNodeId(
  findings: readonly ValidationError[],
  edges: readonly EdgeEndpoints[],
): Readonly<Record<string, readonly ValidationError[]>> {
  const edgesById = new Map<string, EdgeEndpoints>();
  for (const edge of edges) edgesById.set(edge.id, edge);
  const out: Record<string, ValidationError[]> = {};
  const push = (nodeId: string, e: ValidationError) => {
    out[nodeId] ??= [];
    out[nodeId].push(e);
  };
  for (const e of findings) {
    if (e.nodeId) {
      push(e.nodeId, e);
      continue;
    }
    if (!e.edgeId) continue;
    const edge = edgesById.get(e.edgeId);
    if (!edge) continue;
    push(edge.target, e);
  }
  return out;
}
